<template>
  <b-card
    class="upsell-card"
    :style="{
      'background-image': imageWithOverlay,
    }"
  >
    <div
      class="d-flex flex-column flex-lg-row"
      style="height: 100%"
    >
      <div
        class="ml-3 my-5 mr-3 ml-lg-5 mr-lg-0"
      >
        <div class="d-flex justify-content-between d-lg-block">
          <div class="d-inline d-lg-block">
            <h1
              style="font-family: 'hatton-medium'"
              class="tw-w-[200px] sm:tw-w-full"
            >
              <span v-html="UPSELL_DISPLAY_BY_PRODUCT[variant].title"></span>
            </h1>
            <p
              v-if="upsellItem.firstorder"
              class="tw-mb-1"
            >
              {{ upsellPrice }} - limited time only
            </p>
            <p
              v-else
              class="tw-mb-1"
            >
              <span
                v-if="upsellItem.firstorder || upsellItem.upsellcode === 'BLOSSOM'"
              >Was {{ upsellItem.metadata.strikethroughprice }}, now</span>
              {{ upsellPrice }}
              <span v-if="!upsellItem.firstorder && upsellItem.upsellcode !== 'BLOSSOM'">{{ upsellItem.metadata.upsellpriceunit }}</span>
            </p>
            <p
              v-if="upsellItem.upsellcode === 'CREAM'"
              class="tw-text-[#1a6a72]"
            >
              <template v-if="upsellItem.metadata.bfcm === 'true'">
                Auto-refilled at normal price of {{ upsellItem.metadata.strikethroughprice }}
              </template>
              <template v-else-if="upsellItem.firstorder">
                Auto-refilled at normal price of {{ upsellItem.metadata.strikethroughprice }}/{{ upsellItem.metadata.interval }}
              </template>
            </p>
            <p
              v-else-if="upsellItem.upsellcode === 'MINOXIDIL'"
              class="tw-text-[#1a6a72]"
            >
              <template v-if="upsellItem.firstorder">
                Auto-refilled at normal price of {{ upsellItem.metadata.strikethroughprice }}/{{ upsellItem.metadata.interval }}
              </template>
            </p>
            <p
              v-else-if="upsellItem.upsellcode === 'DEEPSLEEP'"
              class="tw-text-[#1a6a72]"
            >
              <template v-if="upsellItem.firstorder">
                Auto-refilled at normal price of {{ upsellItem.metadata.strikethroughprice }}/{{ upsellItem.metadata.interval }}
              </template>
            </p>
            <p
              v-else
              class="tw-text-[#1a6a72]"
            >
              One time purchase. Subsequent orders at full price of {{ upsellItem.metadata.strikethroughprice }}
            </p>
          </div>

          <!-- Badge - Mobile -->
          <div
            v-if="upsellItem.metadata.bfcm === 'true' && !['MINOXIDIL', 'DEEPSLEEP'].includes(upsellItem.upsellcode)"
            class="m-4 d-flex d-lg-none"
          >
            <BFCMBadge
              :upsell-price="upsellPrice"
              :variant="variant"
              class="tw-scale-75 -tw-top-12 -tw-right-8 tw-size-[180px] tw-rotate-[20deg] tw-absolute tw-z-20"
            />
          </div>
          <div
            v-else
            class="round-badge d-flex flex-column justify-content-center align-items-center d-lg-none text-white"
            :class="[variant === 'BLOSSOM' ? 'round-badge__purple' : 'tw-border-solid tw-border-[4px] tw-border-[#cdfcb1]']"
          >
            <span
              v-if="upsellItem.firstorder || upsellItem.upsellcode === 'BLOSSOM'"
              class="badge-strike mb-1"
            >Was {{ upsellItem.metadata.strikethroughprice }}, now</span>
            <span class="badge-price">
              {{ upsellPrice }}
            </span>
            <span
              v-if="!upsellItem.firstorder && upsellItem.upsellcode !== 'BLOSSOM'"
              class="text-center badge-subtitle mt-3"
            >
              {{ upsellItem.metadata.upsellpriceunit }}
            </span>
          </div>
        </div>
        <p
          class="description"
          :class="variant === 'MINOXIDIL' || variant === 'DEEPSLEEP' ? 'bordered-photo-description' : ''"
          style="white-space: pre-line"
        >
          {{ descriptionText }}
        </p>

        <b-button
          variant="info"
          size="lg"
          pill
          class="px-5 my-3 d-none d-sm-block tw-font-light slight-rounded"
          :disabled="processing"
          @click="$emit('order', upsellItem)"
        >
          {{ buttonText }}
        </b-button>
        <b-button
          variant="info"
          size="lg"
          block
          pill
          class="px-5 my-3 d-block d-sm-none tw-font-light slight-rounded"
          :disabled="processing"
          @click="$emit('order', upsellItem)"
        >
          {{ buttonText }}
        </b-button>
        <a
          :href="learnMoreLink"
          class="upsell-link text-uppercase align-middle text-underline"
          target="_blank"
        >
          Learn more about <span>{{ UPSELL_DISPLAY_BY_PRODUCT[variant].name }}</span>
          <span class="next-arrow-purple align-middle"></span>
        </a>
      </div>
      <div
        v-if="upsellItem.metadata.bfcm === 'true' && upsellItem.upsellcode !== 'MINOXIDIL'"
        class="m-4 d-none d-lg-flex"
      >
        <BFCMBadge
          class="tw-size-[180px] tw-rotate-[20deg] tw-absolute -tw-top-7 -tw-right-7 tw-z-20"
          :upsell-price="upsellPrice"
          :variant="variant"
        />
      </div>
      <div
        v-else
        class="m-4 d-none d-lg-flex"
        :class="variant === 'MINOXIDIL' || variant === 'DEEPSLEEP' ? 'tw-rounded-xl tw-justify-end col-4 p-0' : ''"
        :style="variant === 'MINOXIDIL' || variant === 'DEEPSLEEP' ? { 'background-image': imagePlain, 'background-size': 'cover', 'background-position': 'center' } : {}"
      >
        <!-- Badge - Desktop -->
        <div
          class="round-badge text-white d-flex flex-column justify-content-center align-items-center align-items-center mt-n2"
          :class="[
            variant === 'BLOSSOM' ? 'round-badge__purple align-self-end' : 'tw-border-solid tw-border-[6px] tw-border-[#cdfcb1]',
          ]"
        >
          <span
            v-if="upsellItem.firstorder || upsellItem.upsellcode === 'BLOSSOM'"
            class="badge-strike-small mb-2"
          >
            Was {{ upsellItem.metadata.strikethroughprice }}, now
          </span>
          <span
            class="badge-price mt-n2"
          >
            {{ upsellPrice }}
          </span>
          <div
            v-if="!upsellItem.firstorder && upsellItem.upsellcode !== 'BLOSSOM'"
            class="text-center badge-subtitle mt-3"
            style="margin: 0 auto;"
          >
            {{ upsellItem.metadata.upsellpriceunit }}
          </div>
        </div>
      </div>
      <div
        class="pt-8 pb-6 mobile-image tw-h-[430px]"
        :style="{
          'margin-top': 'auto',
          'background-image': imageWithOverlayMobile,
          'background-position': 'center'
        }"
      ></div>
    </div>
  </b-card>
</template>

<script>
// assets
import minoxidilImage from '@/assets/images/minoxidil.png';
import minoxidilCroppedImage from '@/assets/images/minoxidil_cropped.png';
import creamImage from '@/assets/images/web_Winona_r1_03F_83231.svg';
import blossomImage from '@/assets/images/web_winona_r2_32817_1.svg';
import deepSleepCarouselImage from '@/assets/images/winona_deepsleep_carousel.png';
import BFCMBadge from '@/components/dashboard/bfcm/BFCMBadge.vue';
import { UPSELL_DISPLAY_BY_PRODUCT } from '@/constants'
export default {
  name: 'UpsellDisplayCard',
  components: {
    BFCMBadge
  },
  props: {
    processing: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: 'CREAM',
    },
    upsellItem: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    UPSELL_DISPLAY_BY_PRODUCT: () => UPSELL_DISPLAY_BY_PRODUCT,
    imagePlain() {
      const url = this.getBackgroundImage();
      return 'url(' + url + ')';
    },
    imageWithOverlay() {
      if (this.variant === 'MINOXIDIL' || this.variant === 'DEEPSLEEP') {
        return ''
      }

      const url = this.getBackgroundImage();
      const overlay = '-webkit-linear-gradient(left, rgba(247, 247, 247, 0.9) 45%, rgba(247, 247, 247, 0.0) 70%), ';

      return overlay + 'url(' + url + ')';
    },
    imageWithOverlayMobile() {
      let url;
      if (this.variant === 'MINOXIDIL') {
        url = minoxidilCroppedImage;
      } else if (this.variant === 'DEEPSLEEP') {
        url = deepSleepCarouselImage
      } else {
        url = this.getBackgroundImage();
      }
      const overlay = '-webkit-linear-gradient(top, rgba(247, 247, 247, 1.0) 0%,rgba(247, 247, 247, 0.0) 20%), ';
      return overlay + 'url(' + url + ')';
    },
    upsellPrice() {
      if (this.upsellItem.firstorder) {
        return this.upsellItem.metadata.firsttime_upsellprice || this.upsellItem.metadata.upsellprice;
      }

      return this.upsellItem.metadata.upsellprice;
    },
    buttonText() {
      return this.upsellItem.firstorder ? 'Get Deal' : 'Order again';
    },
    descriptionText() {
      return (this.upsellItem.description?.text || '').replace(/\\n/g, '\n\n');
    },
    learnMoreLink() {
      if (this.variant === 'MINOXIDIL') {
        return process.env.VUE_APP_SERVER === 'production' ? 'https://bywinona.com/product/minoxidil-hair-serum' : 'https://staging-nuxt3.bywinona.com/product/minoxidil-hair-serum'
      }

      if (this.variant === 'DEEPSLEEP') {
        return process.env.VUE_APP_SERVER === 'production' ? 'https://bywinona.com/product/menopause-sleep-aid' : 'https://staging-nuxt3.bywinona.com/product/menopause-sleep-aid'
      }

      if (this.upsellItem.metadata.bfcm === 'true') {
        return process.env.VUE_APP_SERVER === 'production' ? 'https://bywinona.com/product/estriol-cream-with-tretinoin-galentines' : 'https://staging-nuxt3.bywinona.com/product/estriol-cream-with-tretinoin-galentines'
      }

      return this.upsellItem.metadata.new_url;
    },
  },
  methods: {
    getBackgroundImage() {
      switch (this.variant) {
        case 'MINOXIDIL':
          return minoxidilImage;
        case 'BLOSSOM':
          return blossomImage;
        case 'DEEPSLEEP':
          return deepSleepCarouselImage;
        default:
          return creamImage;
      }
    }
  },
};
</script>

<style lang="scss">
.upsell-card {
  text-align: left;
  height: 100%;
  border-radius: 24px;
  background-repeat: no-repeat;
  background-position: bottom center;
  background-color: rgb(247, 247, 247);

  @include media-breakpoint-up(md) {
    background-position: 100% 50%;
    background-size: cover;
  }

  @include media-breakpoint-down(md) {
    background-image: none !important;
  }

  .card-body {
    padding: 0 !important;
  }

  .mobile-image {
    height: 430px;

    @include media-breakpoint-down(md) {
      border-bottom-left-radius: 24px;
      border-bottom-right-radius: 24px;
    }

    @include media-breakpoint-down(sm) {
      height: 300px;
    }

    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
  }

  h1 {
    font-size: 2rem;
    @include media-breakpoint-up(lg) {
      font-size: 3rem;
    }
    margin-bottom: 1rem;
  }

  .body {
    padding: 1rem;
    @include media-breakpoint-up(lg) {
      padding: 3rem 0 3rem 4rem;
    }
  }

  .description {
    font-size: 1.25rem;
    margin-top: 1rem;
    width: 100%;
    @include media-breakpoint-up(lg) {
      width: 40%;
    }

    &.bordered-photo-description {
      @include media-breakpoint-up(lg) {
        width: 65%;
      }
    }
  }

  .upsell-link {
    font-family: "Atten New";
    font-weight: 400;
    font-size: 1rem;
    @include media-breakpoint-down(lg) {
      font-size: 0.75rem;
    }
    letter-spacing: 2px;
  }

  .round-badge {
    @include media-breakpoint-up(lg) {
      height: 200px;
      width: 200px;
      left: 65px;
      top: -65px;
    }
    @include media-breakpoint-down(lg) {
      height: 125px;
      width: 125px;
      min-width: 75px;
    }

    @include media-breakpoint-down(md) {
      height: 100px;
      width: 100px;
      min-width: 75px;
    }

    @include media-breakpoint-down(sm) {
      height: 75px;
      width: 75px;
      min-width: 75px;
    }

    background-color: $primary;
    display: block;
    position: relative;
    border-radius: 50%;
    text-align: center;

    &__purple {
      background-color: #2b2660;
      left: 0px;
      top: 0px;
    }

    -webkit-transform: rotate(6deg);
    -moz-transform: rotate(6deg);
    -o-transform: rotate(6deg);
    -ms-transform: rotate(6deg);
    transform: rotate(6deg);

    .badge-strike-small {
      @include media-breakpoint-up(lg) {
        font-size: 1rem;
        height: 20px;
      }
      @include media-breakpoint-down(lg) {
        font-size: 0.75rem;
        height: 10px;
      }
    }

    .badge-strike {
      vertical-align: top;
      @include media-breakpoint-up(lg) {
        font-size: 1.75rem;
        height: 20px;
      }
      @include media-breakpoint-down(lg) {
        font-size: 1rem;
        height: 15px;
      }

      @include media-breakpoint-down(md) {
        font-size: 0.75rem;
        height: 10px;
      }

      @include media-breakpoint-down(sm) {
        font-size: 0.6rem;
        height: 10px;
      }
    }

    .badge-price {
      @include media-breakpoint-up(lg) {
        font-size: 3rem;
        height: 50px;
      }
      @include media-breakpoint-down(lg) {
        font-size: 2rem;
        height: 27px;
      }
      @include media-breakpoint-down(md) {
        font-size: 1.5rem;
        height: 20px;
      }
    }

    .badge-subtitle {
      @include media-breakpoint-up(lg) {
        font-size: 0.75rem;
        width: 70px;
      }
      @include media-breakpoint-down(lg) {
        font-size: 0.5rem;
        width: 50px;
      }
    }
  }
}
</style>
